import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { HttpUtilService } from './http-util.service';
import { EnvironmentService } from '@services/environment.service';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { NotificationsService } from './notifications.service';
import { HttpClient } from '@angular/common/http';
import { NotificationMessageIcon } from '@app/framework/notification-sidebar/notification-sidebar.component';
import { IntegrationService } from './integration.service';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class BatchDetailsService {
  public hasLoadedStatusProgress = false;
  public hasLoadedBatchList = false;
  public selectedBatches = [];
  public queryParameters = '';
  // until sockets can be trusted, there are two pairs of states that need to be alligned when one changes - search made on a list could have details there that differ from what the other state is displaying
  public triggerBatchesState$ = new BehaviorSubject<string | null>(null);
  public triggerBatchState$ = new BehaviorSubject<string | null>(null);

  public _batchList = new BehaviorSubject({
    isProcessing: false,
    isLoading: true,
    hasError: false,
    data: [],
    totalCount: 0,
  });
  public _statusProgress = new BehaviorSubject({
    isLoading: true,
    hasError: false,
    data: {
      pulled: 0,
      pushed: 0,
      queued: 0,
      total: 0,
      contracted: 0,
    },
  });
  public statusProgressQueryParameters;
  public isDownloadingCSV;
  public batchList$ = this._batchList.asObservable();
  public statusProgress$ = this._statusProgress.asObservable();
  public batchCoursesQueryParameters: any;
  public batchListQueryParameters: any;
  constructor(
    private translationService: TranslationService,
    private httpClient: HttpClient,
    private notification: NotificationsService,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private integrationService: IntegrationService
  ) {
    this.environmentService
      .watchTypeChange()
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        // when instance type changes, reset state if loaded
        if (response) {
          if (this.hasLoadedBatchList || this.hasLoadedStatusProgress) {
            this.triggerBatchesState$.next('');
            this._batchList.next({
              isProcessing: false,
              isLoading: true,
              hasError: false,
              data: [],
              totalCount: 0,
            });
            this._statusProgress.next({
              isLoading: true,
              hasError: false,
              data: {
                pulled: 0,
                pushed: 0,
                queued: 0,
                total: 0,
                contracted: 0,
              },
            });
          }
        }
      });
  }

  getBatchCount$() {
    // call to get batch count
    let url = `${this.environmentService.environment.apiRoot}/import-sets?pageNumber=0;perPage=1`;
    return this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })));
  }

  getBatchList$(queryParameters?: string) {
    // get batch list
    this.batchListQueryParameters = queryParameters;
    const polling$ = this.triggerBatchesState$.pipe(
      switchMap(params => this.getBatchListNetworkCall(params))
    );
    return polling$.pipe(switchMap(() => this.batchList$));
  }

  getStatusProgress$() {
    const polling$ = this.triggerBatchesState$.pipe(
      switchMap(() => this.getStatusProgressNetworkCall())
    );
    return polling$.pipe(switchMap(() => this.statusProgress$));
  }
  getSingleBatch$(id) {
    const url = `${this.environmentService.environment.apiRoot}/import-sets/${id}`;
    const polling$ = this.triggerBatchState$.pipe(
      switchMap(() => this.http.request('get', url)),
      catchError(error => of({ error }))
    );
    return polling$;
  }

  getSingleBatchCourses$(id, queryParameters) {
    let url = `${this.environmentService.environment.apiRoot}/import-sets/${id}/courses`;
    if (queryParameters) url += `?${queryParameters}`;
    const polling$ = this.triggerBatchState$.pipe(
      switchMap(() => this.http.request('get', url)),
      catchError(error => of({ error }))
    );
    return polling$;
  }

  getDownloadBatchCsv(queryParameters = '') {
    this.isDownloadingCSV = true;
    let url = `${this.environmentService.environment.apiRoot}/status/courses/csv`;
    if (queryParameters) url += `?${queryParameters}`;
    this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        if (response.error) {
          this.isDownloadingCSV = false;
          return this.notification.popNotification(
            '',
            'Error Downloading CSV: ' + response.error
          );
        }
        if (!response?.url) {
          return this.notification.popNotification(
            '',
            'Error Downloading CSV, no url found'
          );
        }
        this.downloadBatchCSV(response?.url, response?.name);
      });
  }

  getBatchListNetworkCall(queryParameters?) {
    // make network call that will update _batchList state
    this._batchList.next({
      ...this._batchList.getValue(),
      isProcessing: true,
      hasError: false,
    });

    let url = `${this.environmentService.environment.apiRoot}/import-sets`;
    if (queryParameters) url += `?${queryParameters}`;
    return this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap((response: any) => {
          this._batchList.next({
            isProcessing: false,
            hasError: !!response.error,
            isLoading: false,
            totalCount: response?.totalItems || 0,
            data:
              response?.error || !response.listItems
                ? []
                : Array.isArray(response.listItems)
                  ? response.listItems
                  : [response],
          });
        })
      );
  }

  resumeBatch$(batchId, integrationId) {
    const url = `${this.environmentService.environment.apiRoot}/import-sets/${batchId}/resume`;
    return this.http
      .request('put', url)
      .pipe(catchError(error => of(error)))
      .pipe(
        tap(response => {
          if (response?.status === 'ok') {
            this.integrationService.removePausedImportSet(
              String(integrationId),
              batchId
            );
          }
        })
      );
  }

  pauseBatch$(batchId, integrationId) {
    const url = `${this.environmentService.environment.apiRoot}/import-sets/${batchId}/pause`;
    return this.http
      .request('put', url)
      .pipe(catchError(error => of(error)))
      .pipe(
        tap(response => {
          if (response?.status === 'ok') {
            this.integrationService.addPausedImportSet(
              String(integrationId),
              batchId
            );
          }
        })
      );
  }

  abortBatch$(id) {
    const url = `${this.environmentService.environment.apiRoot}/import-sets/${id}/abort`;
    return this.http
      .request('put', url)
      .pipe(catchError(error => of(error)))
      .pipe(tap(response => {}));
  }

  getStatusProgressNetworkCall(queryParameters?) {
    if (!this.hasLoadedStatusProgress) {
      this.hasLoadedStatusProgress = true;
    }
    let url = `${this.environmentService.environment.apiRoot}/status/progress`;
    if (queryParameters) {
      this.statusProgressQueryParameters = queryParameters;
      url += `?${queryParameters}`;
    }
    return this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap(response => {
          if (response.error) {
            return this._statusProgress.next({
              ...this._statusProgress.getValue(),
              isLoading: false,
              hasError: true,
            });
          }
          this._statusProgress.next({
            hasError: false,
            isLoading: false,
            data: response,
          });
        })
      );
  }

  getTotal(data, showSkipped) {
    const fields = [
      'complete',
      'completed',
      'failed',
      'queued',
      'processing',
      'not_found',
    ];
    if (showSkipped) fields.push('skipped');
    return Object.keys(data).reduce(
      (acc, key) => (!fields.includes(key) ? acc : Number(data[key]) + acc),
      0
    );
  }

  downloadBatchCSV(url, name) {
    this.httpClient
      .get(url, { responseType: 'text' })
      .pipe(catchError(error => of({ error })))
      .subscribe((response: any) => {
        this.isDownloadingCSV = false;
        if (response.error)
          return this.notification.popNotification(
            '',
            'Error Downloading CSV File'
          );
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = name || 'batch-course-download.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        this.notification.addUserNotification(
          {
            icon: NotificationMessageIcon.checkmark,
            id: 'csv',
            text: 'Downloaded ' + a?.download,
            shouldForceOpen: false,
            progress: 100,
          },
          'csv'
        );
        return this.notification.popNotification(
          '',
          this.translationService.getTranslatedContent('downloaded_csv') +
            ': ' +
            a.download
        );
      });
  }
}
